// constraintResolutionStore — per-constraint resolution state for G2.
//
// The G2 gate ships with three abstract decisions (G2-D1/D2/D3) and a
// constraint table. Before this store, when the Observer flagged 10
// constraints for review the architect could expand each row and see
// *what* was wrong (verifier disagreement, suggested category, etc.)
// but the only way to *address* them was a single free-text textarea
// on the G2-D2 decision. That meant 10 distinct findings funneled
// through one prose blob — exactly the friction the user reported.
//
// This store hoists per-constraint resolutions out of the gate so the
// architect can click Keep / Recategorize / Reject on each flagged row
// inline, and the gate's G2-D2 decision is auto-rolled-up from those
// picks (status + free-text answer composed from the per-constraint
// actions). Persisted to localStorage so navigating away/back keeps
// progress, and cleared on Re-emit so a fresh constraint log starts
// with a clean slate.
//
// Backend stays unchanged: the rollup is encoded as a structured
// note in the G2-D2 GateDecision.answer field so the audit trail
// captures exactly which constraint got which treatment without
// requiring a Pydantic schema bump.

import { useMemo } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type ConstraintStatus =
  | 'unreviewed'    // No human action yet (default for flagged rows)
  | 'kept'          // Architect confirmed the extraction is correct as-is
  | 'recategorized' // Architect chose a different category (often the verifier's suggestion)
  | 'rejected'      // Architect says this isn't a real constraint and should be dropped

export interface ConstraintResolution {
  status: ConstraintStatus
  /** When status === 'recategorized' — the new category. */
  newCategory?: string
  /** Optional free-text justification, surfaced into G2-D2.answer. */
  note?: string
}

export type ConstraintResolutionKey = string

export function constraintResolutionKey(
  projectId: string,
  runId: number,
  constraintId: string,
): ConstraintResolutionKey {
  return `${projectId}:${runId}:${constraintId}`
}

interface ConstraintResolutionStore {
  resolutions: Record<ConstraintResolutionKey, ConstraintResolution>
  setResolution: (
    key: ConstraintResolutionKey,
    resolution: ConstraintResolution,
  ) => void
  clearResolution: (key: ConstraintResolutionKey) => void
  /** Drop every resolution for a given (project, run). Called on Re-emit. */
  clearForRun: (projectId: string, runId: number) => void
}

export const useConstraintResolutionStore = create<ConstraintResolutionStore>()(
  persist(
    (set) => ({
      resolutions: {},
      setResolution: (key, resolution) =>
        set((s) => ({ resolutions: { ...s.resolutions, [key]: resolution } })),
      clearResolution: (key) =>
        set((s) => {
          if (!(key in s.resolutions)) return s
          const { [key]: _drop, ...rest } = s.resolutions
          return { resolutions: rest }
        }),
      clearForRun: (projectId, runId) =>
        set((s) => {
          const prefix = `${projectId}:${runId}:`
          let touched = false
          const next: Record<ConstraintResolutionKey, ConstraintResolution> = {}
          for (const [k, v] of Object.entries(s.resolutions)) {
            if (k.startsWith(prefix)) {
              touched = true
              continue
            }
            next[k] = v
          }
          return touched ? { resolutions: next } : s
        }),
    }),
    { name: 'ai_alm_constraint_resolutions_v1' },
  ),
)

/** Stable empty-object sentinel — same trick as gateDraftStore.EMPTY_DRAFT. */
const EMPTY: ConstraintResolution = { status: 'unreviewed' }

/** Read a single constraint's resolution. Defaults to ``unreviewed``. */
export function useConstraintResolution(
  key: ConstraintResolutionKey,
): ConstraintResolution {
  return useConstraintResolutionStore((s) => s.resolutions[key] ?? EMPTY)
}

/** Hook that returns a snapshot of every resolution belonging to a
 *  (project, run) as a Map keyed by constraint id.
 *
 *  ⚠ Implementation note: we cannot let the zustand selector itself
 *  return a freshly-built Map — that would mint a new object on every
 *  render and trip ``useSyncExternalStore``'s snapshot equality check,
 *  yielding "The result of getSnapshot should be cached" + a Maximum
 *  update depth render loop (the same trap documented in
 *  ``gateDraftStore.ts``). Instead we select the raw record (a stable
 *  reference unless something actually changed in the store) and let
 *  the consumer memoise the Map projection with ``useMemo``. */
export function useConstraintResolutionsForRun(
  projectId: string,
  runId: number,
): Map<string, ConstraintResolution> {
  const resolutions = useConstraintResolutionStore((s) => s.resolutions)
  return useMemo(() => {
    const out = new Map<string, ConstraintResolution>()
    const prefix = `${projectId}:${runId}:`
    for (const [k, v] of Object.entries(resolutions)) {
      if (k.startsWith(prefix)) {
        out.set(k.slice(prefix.length), v)
      }
    }
    return out
  }, [resolutions, projectId, runId])
}
